import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Mount <SessionAnalyticsProvider> once, above your router, e.g. in
// index.js / App.js:
//
//   <SessionAnalyticsProvider>
//     <BrowserRouter>
//       <App />
//     </BrowserRouter>
//   </SessionAnalyticsProvider>
//
// Because it lives above the router it never remounts on navigation, so
// "time on page" below is really "time since they landed on the site" and
// survives moving between routes — which is what was asked for. Any page
// can then read/write it with `useCtaTracking()` (or the fuller
// `useSessionAnalytics()`) without any extra prop-drilling.

const STORAGE_KEY = "cc_session_analytics_tags";
const EVENTS_STORAGE_KEY = "cc_session_analytics_events";

// Google Ads click IDs plus the usual UTM set. gclid/gbraid/wbraid cover
// Google Ads depending on the click's network/consent path; msclkid and
// fbclid are thrown in too since they're free and the same shape.
const TAG_PARAMS = [
  "gclid",
  "gclsrc",
  "gbraid",
  "wbraid",
  "dclid",
  "msclkid",
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

function readStoredTags() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

function captureGoogleTags() {
  // Merge onto whatever was already captured this session — a gclid
  // picked up on the landing page should survive even if a later page
  // is visited without it on the URL.
  const stored = readStoredTags();
  const params = new URLSearchParams(window.location.search);
  const next = { ...stored };
  let changed = false;
  TAG_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      next[key] = value;
      changed = true;
    }
  });
  if (changed) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      // sessionStorage unavailable (private browsing etc) — tags just
      // won't survive a full page reload, in-memory state still works.
    }
  }
  return next;
}

function readStoredEvents() {
  try {
    const raw = sessionStorage.getItem(EVENTS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

const SessionAnalyticsContext = createContext(null);

export const SessionAnalyticsProvider = ({ children }) => {
  const sessionStart = useRef(Date.now());
  const maxScrollDepth = useRef(0);

  const [googleTags] = useState(() => captureGoogleTags());
  const [events, setEvents] = useState(() => readStoredEvents());

  // Track the deepest scroll reached, as a % of the scrollable height, so
  // a click event can report "how far they'd scrolled" even if they'd
  // since scrolled back up.
  useEffect(() => {
    const updateDepth = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(doc.scrollHeight - doc.clientHeight, 1);
      const pct = Math.min(
        100,
        Math.round(((window.scrollY || doc.scrollTop || 0) / scrollable) * 100),
      );
      if (pct > maxScrollDepth.current) maxScrollDepth.current = pct;
    };
    updateDepth();
    window.addEventListener("scroll", updateDepth, { passive: true });
    window.addEventListener("resize", updateDepth);
    return () => {
      window.removeEventListener("scroll", updateDepth);
      window.removeEventListener("resize", updateDepth);
    };
  }, []);

  // `location` is a short free-text label for where on the page the CTA
  // lives — "hero", "packages", "portfolio", "reviews", etc. `extra` lets
  // a specific call site attach anything else worth knowing (e.g. which
  // package was clicked) without changing the shape everywhere else.
  const trackCtaClick = useCallback(
    (location, extra) => {
      const doc = document.documentElement;
      const scrollable = Math.max(doc.scrollHeight - doc.clientHeight, 1);
      const scrollY = window.scrollY || doc.scrollTop || 0;
      const scrollPct = Math.min(100, Math.round((scrollY / scrollable) * 100));
      if (scrollPct > maxScrollDepth.current)
        maxScrollDepth.current = scrollPct;

      const event = {
        location,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
        dwellMs: Date.now() - sessionStart.current,
        scrollY,
        scrollDepthPct: maxScrollDepth.current,
        googleTags,
        ...(extra || {}),
      };

      setEvents((prev) => {
        const next = [...prev, event];
        try {
          sessionStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(next));
        } catch (e) {
          // ignore — in-memory state still has it for this page load
        }
        return next;
      });

      return event;
    },
    [googleTags],
  );

  const value = useMemo(
    () => ({
      events,
      googleTags,
      trackCtaClick,
      sessionStart: sessionStart.current,
    }),
    [events, googleTags, trackCtaClick],
  );

  return (
    <SessionAnalyticsContext.Provider value={value}>
      {children}
    </SessionAnalyticsContext.Provider>
  );
};

// Full context — events so far, captured Google tags, and the tracker
// itself. Handy at the point you actually submit the lead form, to send
// everything gathered up to then.
export const useSessionAnalytics = () => {
  const ctx = useContext(SessionAnalyticsContext);
  if (!ctx) {
    throw new Error(
      "useSessionAnalytics must be used within a SessionAnalyticsProvider",
    );
  }
  return ctx;
};

// Convenience for the common case: a CTA only needs to fire
// trackCtaClick(location) and doesn't care about the rest of the state.
export const useCtaTracking = () => useSessionAnalytics().trackCtaClick;

export default SessionAnalyticsProvider;
